import { useCallback, useState } from 'react';
import { useMenu } from '@/contexts/MenuContext';
import { useRecipes } from '@/contexts/RecipesContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const useMenuGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMealType, setGeneratingMealType] = useState<string | null>(null);
  const { weeklyMenu, addRecipeToMenu, availableMealTypes, removeRecipeFromMenu, clearMenu } = useMenu();
  const { recipes } = useRecipes();
  const { user } = useAuth();

  // Calculate calories per meal based on total daily calories and number of meals
  const calculateMealCalories = useCallback((mealType: string, totalDailyCalories: number, mealTypes: string[]) => {
    // ENFORCE MINIMUM 1500 CALORIES
    const enforcedCalories = Math.max(1500, totalDailyCalories);
    
    if (enforcedCalories !== totalDailyCalories) {
      console.log(`🔒 MENU GENERATION: Enforcing minimum 1500 calories (was ${totalDailyCalories})`);
    }
    
    const mealCount = mealTypes.length;
    
    if (mealCount <= 3) {
      return Math.round(enforcedCalories / mealCount);
    } else if (mealCount === 4) {
      const distribution = {
        'breakfast': 0.25,
        'lunch': 0.35,
        'afternoon_snack': 0.15,
        'dinner': 0.25
      };
      return Math.round(enforcedCalories * (distribution[mealType] || 0.25));
    } else if (mealCount === 5) {
      const distribution = {
        'breakfast': 0.20,
        'morning_snack': 0.10,
        'lunch': 0.30,
        'afternoon_snack': 0.15,
        'dinner': 0.25
      };
      return Math.round(enforcedCalories * (distribution[mealType] || 0.20));
    } else {
      const isMainMeal = ['breakfast', 'lunch', 'dinner'].includes(mealType);
      if (isMainMeal) {
        return Math.round(enforcedCalories * 0.25);
      } else {
        const remainingCalories = enforcedCalories * 0.25;
        const snackCount = mealCount - 3;
        return Math.round(remainingCalories / snackCount);
      }
    }
  }, []);

  // Get recipes used in the current day only
  const getUsedRecipesInDay = useCallback((day: string) => {
    if (!weeklyMenu?.days?.[day]) return [];
    
    const usedRecipeIds: string[] = [];
    const dayData = weeklyMenu.days[day];
    
    for (const mealType of availableMealTypes) {
      const meals = dayData[mealType] || [];
      meals.forEach(meal => {
        if (meal.recipeId && !usedRecipeIds.includes(meal.recipeId)) {
          usedRecipeIds.push(meal.recipeId);
        }
      });
    }
    
    return usedRecipeIds;
  }, [weeklyMenu, availableMealTypes]);

  // Get recipes used across the week for better variety tracking
  const getUsedRecipesInWeek = useCallback(() => {
    if (!weeklyMenu?.days) return [];
    
    const usedRecipeIds: string[] = [];
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    for (const day of allDays) {
      const dayData = weeklyMenu.days[day];
      if (dayData) {
        for (const mealType of availableMealTypes) {
          const meals = dayData[mealType] || [];
          meals.forEach(meal => {
            if (meal.recipeId && !usedRecipeIds.includes(meal.recipeId)) {
              usedRecipeIds.push(meal.recipeId);
            }
          });
        }
      }
    }
    
    return usedRecipeIds;
  }, [weeklyMenu, availableMealTypes]);

  const generateMealForSlot = useCallback(async (day: string, mealType: string, targetCalories: number, excludeRecipeIds: string[] = [], forceVariety: boolean = false) => {
    console.log(`🚀 Generando receta para ${day} - ${mealType} con ${targetCalories} kcal`);

    if (!recipes || recipes.length === 0) {
      console.error('❌ No hay recetas disponibles');
      toast.error('No hay recetas disponibles');
      return null;
    }

    // FIXED TAG MAPPING - usando las etiquetas correctas que están en la base de datos
    const strictMealTypeMap: Record<string, string[]> = {
      'breakfast': ['desayuno', 'breakfast'],
      'morning_snack': ['merienda', 'snack', 'tentempie'], // CAMBIADO: merienda en singular
      'lunch': ['comida', 'lunch', 'almuerzo'],
      'afternoon_snack': ['merienda', 'snack', 'tentempie'], // CAMBIADO: merienda en singular
      'dinner': ['cena', 'dinner'],
      'evening_snack': ['merienda', 'snack', 'tentempie'] // CAMBIADO: merienda en singular
    };

    const requiredTags = strictMealTypeMap[mealType];
    
    if (!requiredTags) {
      console.error(`❌ No hay etiquetas definidas para ${mealType}`);
      toast.error(`No hay etiquetas definidas para ${mealType}`);
      return null;
    }

    console.log(`🏷️ Buscando recetas con etiquetas OBLIGATORIAS: ${requiredTags.join(', ')} para ${mealType}`);
    
    // DEBUG: Let's see ALL recipes and their tags for snacks
    if (mealType.includes('snack') || mealType === 'afternoon_snack') {
      console.log(`🔍 DEBUGGING MERIENDAS - Total de recetas disponibles: ${recipes.length}`);
      
      recipes.forEach((recipe, index) => {
        console.log(`📋 Receta ${index + 1}: "${recipe.title}"`);
        console.log(`   - ID: ${recipe.id}`);
        console.log(`   - Etiquetas: ${recipe.tags ? recipe.tags.join(', ') : 'SIN ETIQUETAS'}`);
        console.log(`   - Tipo de etiquetas: ${recipe.tags ? typeof recipe.tags : 'undefined'}`);
        console.log(`   - Es array: ${Array.isArray(recipe.tags)}`);
        
        if (recipe.tags && Array.isArray(recipe.tags)) {
          const hasSnackTag = recipe.tags.some(tag => 
            typeof tag === 'string' && 
            ['merienda', 'snack', 'tentempie'].some(reqTag => // CAMBIADO: merienda en singular
              tag.toLowerCase() === reqTag.toLowerCase()
            )
          );
          console.log(`   - ¿Tiene etiqueta de merienda?: ${hasSnackTag ? '✅' : '❌'}`);
        }
        console.log('---');
      });
    }
    
    // STEP 1: Filter by REQUIRED tags FIRST - NO COMPROMISES
    let recipesWithCorrectTags = recipes.filter(recipe => {
      if (!recipe.tags || !Array.isArray(recipe.tags)) {
        console.log(`❌ Receta "${recipe.title}" no tiene etiquetas válidas`);
        return false;
      }
      
      const hasRequiredTag = requiredTags.some(requiredTag => 
        recipe.tags.some((tag: string) => 
          typeof tag === 'string' && tag.toLowerCase() === requiredTag.toLowerCase()
        )
      );
      
      if (hasRequiredTag) {
        console.log(`✅ Receta "${recipe.title}" tiene etiqueta válida: ${recipe.tags.join(', ')}`);
      } else {
        console.log(`❌ Receta "${recipe.title}" NO tiene etiquetas válidas para ${mealType}. Etiquetas: ${recipe.tags.join(', ')}`);
      }
      
      return hasRequiredTag;
    });

    console.log(`📊 Recetas con etiquetas correctas para ${mealType}: ${recipesWithCorrectTags.length}/${recipes.length}`);
    
    // DEBUG: If it's a snack type, show exactly which recipes were found
    if (mealType.includes('snack') || mealType === 'afternoon_snack') {
      console.log(`🍪 RECETAS DE MERIENDAS ENCONTRADAS (${recipesWithCorrectTags.length}):`);
      recipesWithCorrectTags.forEach((recipe, index) => {
        console.log(`   ${index + 1}. "${recipe.title}" - Etiquetas: ${recipe.tags?.join(', ')}`);
      });
    }
    
    if (recipesWithCorrectTags.length === 0) {
      console.error(`❌ NO HAY RECETAS CON LAS ETIQUETAS CORRECTAS para ${mealType}`);
      console.error(`❌ Etiquetas requeridas: ${requiredTags.join(', ')}`);
      
      // Mostrar las etiquetas disponibles para debug
      const availableTags = new Set();
      recipes.forEach(recipe => {
        if (recipe.tags && Array.isArray(recipe.tags)) {
          recipe.tags.forEach(tag => availableTags.add(tag));
        }
      });
      console.log(`📋 Etiquetas disponibles en el sistema: ${Array.from(availableTags).join(', ')}`);
      
      toast.error(`No hay recetas de ${mealType} disponibles. Verifica que las recetas tengan las etiquetas correctas: ${requiredTags.join(', ')}`);
      return null;
    }

    // STEP 2: Apply exclusion filters ONLY on recipes with correct tags
    let availableRecipes = [];
    
    if (forceVariety) {
      // For "cambiar receta": exclude only the current recipe being replaced
      availableRecipes = recipesWithCorrectTags.filter(recipe => !excludeRecipeIds.includes(recipe.id));
      console.log(`🔄 Cambiar receta - Opciones disponibles: ${availableRecipes.length}`);
    } else {
      // For full day generation: exclude recipes used in the same day
      const dailyUsedRecipes = getUsedRecipesInDay(day);
      availableRecipes = recipesWithCorrectTags.filter(recipe => !dailyUsedRecipes.includes(recipe.id));
      
      // If we don't have enough variety in the day, just exclude the ones passed as parameter
      if (availableRecipes.length < 3) {
        availableRecipes = recipesWithCorrectTags.filter(recipe => !excludeRecipeIds.includes(recipe.id));
      }
      
      console.log(`📅 Generar día - Opciones disponibles: ${availableRecipes.length}`);
    }

    // If still no recipes after filtering, use all recipes with correct tags
    if (availableRecipes.length === 0) {
      availableRecipes = recipesWithCorrectTags;
      console.log(`⚠️ Usando todas las recetas con etiquetas correctas: ${availableRecipes.length}`);
    }

    if (availableRecipes.length === 0) {
      console.error(`❌ No quedan recetas disponibles para ${mealType} después del filtrado`);
      toast.error(`No hay recetas disponibles para ${mealType}`);
      return null;
    }

    // STEP 3: Find the best recipe with appropriate portion size
    const shuffledRecipes = [...availableRecipes].sort(() => Math.random() - 0.5);
    
    let bestRecipe = null;
    let bestPortion = 1;
    let bestCaloriesDiff = Infinity;

    // Try the first 10 shuffled recipes to find a good match
    const recipesToTry = shuffledRecipes.slice(0, Math.min(10, shuffledRecipes.length));
    
    for (const recipe of recipesToTry) {
      const recipeCalories = recipe.calories || 200;
      const idealPortion = targetCalories / recipeCalories;
      
      // Allow reasonable portion range (0.5x to 2.5x)
      const clampedPortion = Math.max(0.5, Math.min(2.5, idealPortion));
      const resultingCalories = recipeCalories * clampedPortion;
      const caloriesDiff = Math.abs(resultingCalories - targetCalories);
      
      if (caloriesDiff < bestCaloriesDiff) {
        bestCaloriesDiff = caloriesDiff;
        bestRecipe = recipe;
        bestPortion = Math.round(clampedPortion * 10) / 10;
      }
      
      // If we find a really good match, use it immediately
      if (caloriesDiff < 50) {
        break;
      }
    }

    // If still no recipe found, just pick the first available one
    if (!bestRecipe && shuffledRecipes.length > 0) {
      bestRecipe = shuffledRecipes[0];
      const recipeCalories = bestRecipe.calories || 200;
      bestPortion = Math.max(0.5, Math.min(2.5, targetCalories / recipeCalories));
      bestPortion = Math.round(bestPortion * 10) / 10;
    }

    if (bestRecipe) {
      const finalCalories = (bestRecipe.calories || 200) * bestPortion;
      console.log(`✅ Receta seleccionada para ${mealType}: ${bestRecipe.title} (${Math.round(finalCalories)} kcal, porción: ${bestPortion}x)`);
      console.log(`🏷️ Etiquetas de la receta: ${bestRecipe.tags?.join(', ') || 'Sin etiquetas'}`);
      
      try {
        await addRecipeToMenu(bestRecipe.id, day, mealType, bestPortion);
        toast.success(`Receta añadida: ${bestRecipe.title}`);
        return { calories: finalCalories, recipe: bestRecipe.title, recipeId: bestRecipe.id };
      } catch (error) {
        console.error('❌ Error al añadir receta:', error);
        toast.error('Error al añadir la receta');
      }
    } else {
      console.error(`❌ No se encontraron recetas para ${mealType}`);
      toast.error(`No se encontraron recetas para ${mealType}`);
    }

    return null;
  }, [recipes, addRecipeToMenu, getUsedRecipesInDay, availableMealTypes]);

  const generateDayMenu = useCallback(async (day: string) => {
    if (!weeklyMenu || !user) {
      toast.error('No se puede generar el menú');
      return;
    }

    console.log(`🚀 Generando menú completo para ${day}`);
    setIsGenerating(true);
    toast.info(`Generando menú para el día...`);

    try {
      // Clear existing meals for this day first
      if (weeklyMenu.days && weeklyMenu.days[day]) {
        for (const mealType of availableMealTypes) {
          const currentMeals = weeklyMenu.days[day]?.[mealType] || [];
          for (let i = currentMeals.length - 1; i >= 0; i--) {
            await removeRecipeFromMenu(currentMeals[i].recipeId, day, mealType, i);
          }
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));

      const totalDailyCalories = Math.max(1500, weeklyMenu.nutritionGoals?.calories || 2000);

      console.log(`📊 Calorías diarias objetivo (con mínimo enforced): ${totalDailyCalories}`);
      console.log(`🍽️ Tipos de comida disponibles: ${availableMealTypes.join(', ')}`);

      // Generate meals for this day with day-level variety tracking
      const usedRecipeIds: string[] = [];
      
      for (const mealType of availableMealTypes) {
        const targetCalories = calculateMealCalories(mealType, totalDailyCalories, availableMealTypes);
        console.log(`🎯 ${mealType}: ${targetCalories} kcal`);
        
        const result = await generateMealForSlot(day, mealType, targetCalories, usedRecipeIds, false);
        
        if (result && result.recipeId) {
          usedRecipeIds.push(result.recipeId);
        }
        
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      toast.success('Menú del día generado exitosamente');
    } catch (error) {
      console.error('❌ Error generando menú:', error);
      toast.error('Error al generar el menú');
    } finally {
      setIsGenerating(false);
    }
  }, [weeklyMenu, user, availableMealTypes, generateMealForSlot, calculateMealCalories, removeRecipeFromMenu]);

  const changeMealRecipe = useCallback(async (day: string, mealType: string) => {
    if (!weeklyMenu) {
      toast.error('Menú no disponible');
      return;
    }

    console.log(`🔄 Cambiando receta para ${day} - ${mealType}`);
    setIsGenerating(true);
    setGeneratingMealType(mealType);

    try {
      // Get current recipe to exclude it specifically
      const currentMeals = weeklyMenu.days[day]?.[mealType] || [];
      const currentRecipeIds = currentMeals.map(meal => meal.recipeId);

      // Remove current recipe first
      for (let i = currentMeals.length - 1; i >= 0; i--) {
        await removeRecipeFromMenu(currentMeals[i].recipeId, day, mealType, i);
      }

      await new Promise(resolve => setTimeout(resolve, 200));

      // Calculate target calories for this meal type with minimum enforcement
      const totalDailyCalories = Math.max(1500, weeklyMenu.nutritionGoals?.calories || 2000);
      const targetCalories = calculateMealCalories(mealType, totalDailyCalories, availableMealTypes);

      // Generate new recipe with maximum variety (only exclude current recipe)
      const result = await generateMealForSlot(day, mealType, targetCalories, currentRecipeIds, true);
      
      if (!result) {
        toast.error('No se encontró una receta diferente');
      }

    } catch (error) {
      console.error('❌ Error cambiando receta:', error);
      toast.error('Error al cambiar la receta');
    } finally {
      setIsGenerating(false);
      setGeneratingMealType(null);
    }
  }, [weeklyMenu, availableMealTypes, generateMealForSlot, removeRecipeFromMenu, calculateMealCalories]);

  return {
    isGenerating,
    generateDayMenu,
    changeMealRecipe,
    generatingMealType
  };
};
